import React from 'react';
import {
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Icon,
  Text,
  Avatar,
  Spinner,
  Button,
  IconButton,
} from '@chakra-ui/react';
import { FiSearch, FiAlertCircle, FiX } from 'react-icons/fi';
import { parseQRPayload } from 'utils/qrPayload';
import useManualStudentSearch from '../hooks/useManualStudentSearch';

// Buscador inline que aparece flotando sobre la cámara/lector USB mientras
// Modo Consulta está activo (ver botón redondo de lupa en ScannerShell) —
// antes de esto, Consulta en cámara/USB solo aceptaba un escaneo físico; acá
// se agrega la búsqueda por nombre/RUT como alternativa, sin tener que
// cambiar de dispositivo de lectura. No se muestra en modo "Manual": esa
// pantalla completa (ManualScanner.js) ya ES un buscador, mostrar este
// encima sería redundante. Misma búsqueda y misma fila de resultado (avatar
// + nombre + RUT/grupo) que ManualScanner, vía useManualStudentSearch.
//
// Este <Input> visible es TAMBIÉN el destino del lector físico USB mientras
// Consulta está activa: ScannerShell pausa el input oculto de USBScanner
// durante toda la consulta (isModalOpen), así que lo que el lector "tipee"
// cae acá — ver handleKeyDown abajo, que reconoce el payload de un QR/código
// (parseQRPayload) al presionar Enter y lo entrega directo por onResolved,
// sin pasar por la búsqueda por nombre. Intentar en cambio distinguir "¿el
// usuario está escribiendo o el lector está tipeando?" con foco/blur no
// funciona: el input oculto de USBScanner se reenfocaba a sí mismo DENTRO
// de su propio evento blur, lo que gana la carrera contra el focus del
// click en este input y le impedía recibir el foco — por eso antes no se
// podía escribir acá.
const ConsultaSearchBox = ({ onResolved, onCancel }) => {
  const { term, setTerm, results, searching, resolving, error, pick, retry } = useManualStudentSearch(true, onResolved);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const token = parseQRPayload(term.trim());
    if (token) {
      e.preventDefault();
      setTerm('');
      onResolved(token);
    }
  };

  return (
    <VStack
      bg="sky.ink" borderRadius="20px" px={{ base: 4, md: 6 }} py={{ base: 5, md: 6 }} spacing={4}
      boxShadow="0 10px 32px -8px rgba(0,0,0,.4)" w="full"
    >
      <HStack w="full" justify="space-between" align="center">
        <HStack spacing={2}>
          <Icon as={FiSearch} color="#fff" boxSize={4} flexShrink={0} />
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="14px" fontWeight="800" color="#fff" letterSpacing="0.01em">Modo Consulta</Text>
            <Text fontSize="12px" color="rgba(255,255,255,.85)" fontWeight="500">
              Escanea o busca por nombre/RUT — no registra entrada ni salida
            </Text>
          </VStack>
        </HStack>
        <IconButton
          icon={<Icon as={FiX} boxSize={4} />}
          aria-label="Cancelar consulta"
          title="Cancelar consulta"
          size="sm" variant="ghost" color="#fff"
          _hover={{ bg: 'rgba(255,255,255,.2)' }}
          onClick={onCancel}
        />
      </HStack>

      <InputGroup size="lg" maxW="440px" mx="auto">
        <InputLeftElement h="100%" pointerEvents="none"><Icon as={FiSearch} color="ink.400" /></InputLeftElement>
        <Input
          value={term} onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Nombre, apellido o RUT…"
          autoFocus
          bg="white" color="ink.900" border="none" borderRadius="14px" h="60px" fontSize="16px"
          boxShadow="0 4px 16px -4px rgba(0,0,0,.3)"
          _placeholder={{ color: 'ink.400' }}
          _focus={{ boxShadow: '0 0 0 3px rgba(255,255,255,.55)' }}
          isDisabled={!!resolving}
        />
        {searching && <InputRightElement h="100%"><Spinner size="sm" color="coral.500" /></InputRightElement>}
      </InputGroup>

      {error && (
        <HStack bg="white" border="1px solid" borderColor="rgba(214,69,43,.35)"
          borderRadius="10px" px={3} py={2} w="full" maxW="440px" mx="auto">
          <Icon as={FiAlertCircle} color="danger.500" boxSize={4} flexShrink={0} />
          <Text color="danger.500" fontSize="13.5px" fontWeight="600" flex={1}>{error}</Text>
          <Button size="sm" variant="ghost" color="danger.500" onClick={retry}>Reintentar</Button>
        </HStack>
      )}

      {results.length > 0 && (
        <VStack spacing={2} w="full" maxW="440px" mx="auto" align="stretch" maxH="260px" overflowY="auto">
          {results.map((u) => (
            <HStack key={u.id} as="button" type="button" onClick={() => pick(u)}
              bg="white" border="1px solid" borderColor="cream.line"
              _hover={{ bg: 'coral.tint', borderColor: 'coral.soft' }}
              borderRadius="12px" px={3} py={3} minH="60px" boxShadow="0 2px 10px -4px rgba(0,0,0,.2)"
              spacing={3} pointerEvents={resolving ? 'none' : 'auto'} opacity={resolving && resolving.id !== u.id ? 0.5 : 1}>
              <Avatar size="sm" name={u.name} bg="coral.500" />
              <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                <Text color="ink.900" fontWeight="700" fontSize="14.5px" noOfLines={1}>{u.name}</Text>
                <Text color="ink.400" fontSize="12.5px" noOfLines={1}>{u.username}{u.group ? ` · ${u.group}` : ''}</Text>
              </VStack>
              {resolving?.id === u.id && <Spinner size="sm" color="coral.500" />}
            </HStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default ConsultaSearchBox;
